import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSync, faSearch, faUndo } from '@fortawesome/free-solid-svg-icons';
import BiblioService from '../../services/BiblioService';
import { Loading } from '../Loading';
import Paginator from '../Paginator';
import ConfirmationModal from '../ConfirmationModal';
import { BiblioTable } from './BiblioTable';
import { BiblioModal } from './BiblioModal';
import Autocomplete from '../Autocomplete';

const EMPTY_FILTERS = { prefix: '', search: '' };

export function BiblioStoresTab() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [feedback, setFeedback] = useState('');

    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [drafts, setDrafts] = useState({ prefix: '', search: '' });
    const [availableAccounts, setAvailableAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);

    const [modal, setModal] = useState({ open: false, isNew: true, store: null });
    const [pauseTarget, setPauseTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deployTarget, setDeployTarget] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchStores = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await BiblioService.listStores({
                page, per_page: perPage, ...filters,
            });
            setItems(res.items || []);
            setTotal(res.total || 0);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [page, perPage, filters]);

    const fetchAccounts = useCallback(async () => {
        setLoadingAccounts(true);
        try {
            const res = await BiblioService.listStores({ page: 1, per_page: 1000 });
            const items = res.items || [];
            setAvailableAccounts(items.map((a) => ({ label: `[${a.prefix}] ${a.account}`, value: a.prefix })));
        } catch (e) {
            // ignore
        } finally {
            setLoadingAccounts(false);
        }
    }, []);

    useEffect(() => {
        fetchStores();
        return () => { setItems([]); };
    }, [fetchStores]);

    useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

    const flash = (msg) => {
        setFeedback(msg);
        setTimeout(() => setFeedback(''), 4000);
    };

    const handleSearch = () => {
        setFilters({ prefix: drafts.prefix.toUpperCase(), search: drafts.search });
        setPage(1);
    };

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        setDrafts({ prefix: '', search: '' });
        setPage(1);
    };

    const handleReload = () => fetchStores();

    const openCreate = () => setModal({ open: true, isNew: true, store: null });
    const openEdit = (store) => setModal({ open: true, isNew: false, store });

    const handleSave = async (payload) => {
        setSaving(true);
        try {
            if (modal.isNew) {
                await BiblioService.createStore(payload);
            } else {
                await BiblioService.updateStore(payload);
            }
            if (payload.deploy) {
                flash('Cambios guardados. Despliegue en proceso');
            } else {
                flash(modal.isNew ? 'Tienda guardada correctamente' : 'Tienda actualizada correctamente');
            }
            setModal({ open: false, isNew: true, store: null });
            fetchStores();
            return { ok: true };
        } catch (e) {
            return { ok: false, msg: e.message };
        } finally {
            setSaving(false);
        }
    };

    const handlePause = async () => {
        if (!pauseTarget) return;
        setActionLoading(true);
        try {
            const next = pauseTarget.biblio_active === 1 ? 0 : 1;
            await BiblioService.pauseStore(pauseTarget.prefix, next);
            flash(next === 1 ? 'Tienda activada correctamente' : 'Tienda pausada correctamente');
            setPauseTarget(null);
            fetchStores();
        } catch (e) {
            flash(e.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setActionLoading(true);
        try {
            await BiblioService.deleteStore(deleteTarget.prefix);
            flash('Tienda eliminada correctamente');
            setDeleteTarget(null);
            fetchStores();
        } catch (e) {
            flash(e.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeploy = async () => {
        if (!deployTarget) return;
        setActionLoading(true);
        try {
            await BiblioService.deployStore(deployTarget.prefix);
            flash('Despliegue iniciado. La tienda quedará activa cuando termine el proceso');
            setDeployTarget(null);
            fetchStores();
        } catch (e) {
            flash(e.message);
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <>
            <div className="filters-panel mb-3">
                <div className="row g-3 align-items-end">
                    <div className="col-md-4">
                        <label className="fw-bold mb-1 d-block text-white-50" style={{ fontSize: '0.85rem' }}>Cuenta</label>
                        <Autocomplete
                            options={availableAccounts}
                            value={availableAccounts.find((a) => a.value === drafts.prefix) || null}
                            onChange={(opt) => setDrafts((p) => ({ ...p, prefix: opt ? opt.value : '' }))}
                            isDark
                            isClearable
                            placeholder={loadingAccounts ? 'Cargando...' : 'Buscar cuenta...'}
                        />
                    </div>
                    <div className="col-auto d-flex gap-2">
                        <button className="btn btn-ink-search" onClick={handleSearch}>
                            <FontAwesomeIcon icon={faSearch} className="me-1" /> Buscar
                        </button>
                        <button className="btn btn-outline-secondary border-secondary text-white-50 px-3" onClick={handleReset}>
                            <FontAwesomeIcon icon={faUndo} className="me-1" /> Limpiar
                        </button>
                    </div>
                </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="text-white m-0">Tiendas Biblio</h5>
                <div className="d-flex gap-2">
                    <button className="btn btn-ink-icon" title="Recargar" onClick={handleReload}>
                        <FontAwesomeIcon icon={faSync} />
                    </button>
                    <button className="btn btn-primary" onClick={openCreate}>
                        <FontAwesomeIcon icon={faPlus} className="me-1" /> Registrar tienda
                    </button>
                </div>
            </div>

            {feedback && <div className="alert alert-info py-2">{feedback}</div>}
            {error && <div className="alert alert-danger py-2">{error}</div>}

            {loading ? (
                <Loading />
            ) : (
                <BiblioTable
                    stores={items}
                    onEdit={openEdit}
                    onPause={setPauseTarget}
                    onDeploy={setDeployTarget}
                    onDelete={setDeleteTarget}
                />
            )}

            {total > perPage && (
                <Paginator
                    totalItems={total}
                    itemsPerPage={perPage}
                    currentPage={page}
                    onPageChange={setPage}
                />
            )}

            <BiblioModal
                isOpen={modal.open}
                isNew={modal.isNew}
                store={modal.store}
                collection={items}
                saving={saving}
                onClose={() => setModal({ open: false, isNew: true, store: null })}
                onSave={handleSave}
            />

            <ConfirmationModal
                isOpen={Boolean(pauseTarget)}
                onClose={() => setPauseTarget(null)}
                onConfirm={handlePause}
                title={pauseTarget && pauseTarget.biblio_active === 1 ? 'Pausar tienda' : 'Activar tienda'}
                message={
                    pauseTarget
                        ? (pauseTarget.biblio_active === 1
                            ? `La tienda de ${pauseTarget.account} dejará de estar disponible hasta reactivarla.`
                            : `La tienda de ${pauseTarget.account} volverá a estar disponible.`)
                        : ''
                }
                confirmText={pauseTarget && pauseTarget.biblio_active === 1 ? 'Pausar' : 'Activar'}
                type="warning"
                isLoading={actionLoading}
            />

            <ConfirmationModal
                isOpen={Boolean(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Eliminar tienda"
                message={
                    deleteTarget
                        ? `Se desvinculará la tienda de ${deleteTarget.account} (${deleteTarget.prefix}). Los datos de la tienda no se borran y puede registrarse de nuevo.`
                        : ''
                }
                confirmText="Eliminar"
                type="danger"
                isLoading={actionLoading}
            />

            <ConfirmationModal
                isOpen={Boolean(deployTarget)}
                onClose={() => setDeployTarget(null)}
                onConfirm={handleDeploy}
                title="Desplegar tienda"
                message={
                    deployTarget
                        ? `Se iniciará el despliegue de la tienda de ${deployTarget.account} (${deployTarget.prefix}). El proceso puede tardar y la tienda quedará activa al finalizar.`
                        : ''
                }
                confirmText="Desplegar"
                type="warning"
                isLoading={actionLoading}
            />
        </>
    );
}

export default BiblioStoresTab;
